// Shared, reference-counted EventSource for GET /api/decks/:id/events.
//
// The server already multiplexes every named event (message, delta,
// activity, context, version_created, artifact_updated, ...) onto one
// SSE stream, but three hooks (useDeckChat, useDeckActivity,
// useDeckEvents) each opened their own connection — 3x /events per
// deck page (observed in the network log). One physical connection per
// deck now fans out to any number of subscribers.
//
// Reconnect semantics preserved: on error the shared connection closes
// and retries after 2s (the strictest of the three hooks' policies);
// every subscriber's onOpen fires again with reconnect=true.

interface SubscriberImpl {
  byName: Map<string, Set<(e: MessageEvent) => void>>;
  openFns: Set<(reconnect: boolean) => void>;
  errorFns: Set<() => void>;
}

export interface DeckEventsSubscription {
  /** Listen to a named SSE event on the shared stream. */
  on(name: string, fn: (e: MessageEvent) => void): void;
  /** Fires on every (re)connect; reconnect=true after the first open. */
  onOpen(fn: (reconnect: boolean) => void): void;
  /** Fires when the shared connection drops (before the retry). */
  onError(fn: () => void): void;
  /** Unsubscribe; the physical connection closes with the last subscriber. */
  close(): void;
}

interface Entry {
  es: EventSource | null;
  subs: Set<SubscriberImpl>;
  attachedNames: Set<string>;
  connectCount: number;
  pendingRetry: ReturnType<typeof setTimeout> | null;
  closed: boolean;
}

const RETRY_MS = 2_000;
const entries = new Map<string, Entry>();

function fanOut(entry: Entry, name: string, e: MessageEvent): void {
  for (const sub of entry.subs) {
    const fns = sub.byName.get(name);
    if (fns) for (const fn of fns) fn(e);
  }
}

function attachName(deckId: string, entry: Entry, name: string): void {
  if (entry.attachedNames.has(name)) return;
  entry.attachedNames.add(name);
  entry.es?.addEventListener(name, (e) =>
    fanOut(entry, name, e as MessageEvent),
  );
}

function connect(deckId: string, entry: Entry): void {
  if (entry.closed) return;
  entry.pendingRetry = null;
  const es = new EventSource(`/api/decks/${deckId}/events`, {
    withCredentials: true,
  });
  entry.es = es;
  for (const name of entry.attachedNames) {
    es.addEventListener(name, (e) => fanOut(entry, name, e as MessageEvent));
  }
  es.onopen = () => {
    entry.connectCount += 1;
    const reconnect = entry.connectCount > 1;
    for (const sub of entry.subs) {
      for (const fn of sub.openFns) fn(reconnect);
    }
  };
  es.onerror = () => {
    if (entry.closed) return;
    es.close();
    entry.es = null;
    for (const sub of entry.subs) {
      for (const fn of sub.errorFns) fn();
    }
    entry.pendingRetry = setTimeout(() => connect(deckId, entry), RETRY_MS);
  };
}

export function subscribeDeckEvents(deckId: string): DeckEventsSubscription {
  let entry = entries.get(deckId);
  if (!entry) {
    entry = {
      es: null,
      subs: new Set(),
      attachedNames: new Set(),
      connectCount: 0,
      pendingRetry: null,
      closed: false,
    };
    entries.set(deckId, entry);
    connect(deckId, entry);
  }
  const ent = entry;
  const impl: SubscriberImpl = { byName: new Map(), openFns: new Set(), errorFns: new Set() };
  ent.subs.add(impl);
  let open = true;
  return {
    on(name, fn) {
      if (!open) return;
      let set = impl.byName.get(name);
      if (!set) {
        set = new Set();
        impl.byName.set(name, set);
      }
      set.add(fn);
      attachName(deckId, ent, name);
    },
    onOpen(fn) {
      if (!open) return;
      impl.openFns.add(fn);
      // If already connected, fire immediately so late subscribers get
      // their "connected" signal (readyState OPEN === 1).
      if (ent.es && ent.es.readyState === 1) fn(ent.connectCount > 1);
    },
    onError(fn) {
      if (!open) return;
      impl.errorFns.add(fn);
    },
    close() {
      if (!open) return;
      open = false;
      ent.subs.delete(impl);
      if (ent.subs.size === 0) {
        ent.closed = true;
        if (ent.pendingRetry) clearTimeout(ent.pendingRetry);
        ent.es?.close();
        entries.delete(deckId);
      }
    },
  };
}
